import { ICreateProjectService, SizeObject } from "@components/create-project-dialog/services/create-project-service";
import { IFormInputOption } from "@components/form/interfaces/form-interface";
import { ModelTypes } from "@zeus";
import { inject, watch } from "aurelia";
import { IStep } from "../brand-guide/brand-guide-page";
import { GoPremiumDialog } from "@components/calendar/components/go-premium-dialog/go-premium-dialog";
import { IAuthService } from "@interfaces/auth-service/auth-service-interface";
import { IDialogService } from "@aurelia/dialog";
import { IZeusClient, ZeusClient } from "@services/graphql-service/zeus-client";
import { toastifyError } from "@helpers/toastify";
import { GoogleAnalyticsEventAction, GoogleAnalyticsEventCategory, GoogleAnalyticsHelper } from "@helpers/google-analytics/google-analytics-helper";
import { IRouter } from "@aurelia/router";
import { ViewMoreImagesDialog } from "./components/view-more-images/view-more-images-dialog";

@inject(IZeusClient, IAuthService, IDialogService, ICreateProjectService, IRouter)
export class CreatePostPage {

  private steps: IStep[] = [
    { isCompleted: false, title: 'conteúdo', progressBarRef: null },
    { isCompleted: false, title: 'modelo', progressBarRef: null },
    { isCompleted: false, title: 'imagem', progressBarRef: null }
  ]
  private currentStep: number = 0
  private postDescription: string

  private isLoading: boolean = false

  private brand: ModelTypes['brands']

  private selectedFormat: string;
  private formats: SizeObject[] = []
  private displayFormats: IFormInputOption[] = []

  private selectedTemplate: ModelTypes["pTemplate"];
  private availableImages: ModelTypes["pTemplate"][]

  private selectedImage: string;
  private currentImageTabIndex: number = 0;

  private isViewMoreImages: boolean = false

  constructor(
    private zeusClient: ZeusClient,
    private authService: IAuthService,
    private dialogService: IDialogService,
    private service: ICreateProjectService,
    private router: IRouter
  ) {

  }

  attached() {
    this.getData()
  }

  async getBrandInfo() {
    const response = await this.zeusClient.chain('query')({
      brands: [
        {
          limit: 1
        },
        {
          id: true,
          brandName: true,
          segment: true,
          brandTargetAudience: true
        }
      ]
    })

    if (!response.brands) throw new Error("Não foi possível encontrar sua marca!")

    this.brand = response.brands.at(0) as ModelTypes['brands']
  }

  async generatePostIdea() {

    try {

      this.isLoading = true

      const response = await this.zeusClient.chain('query')({
        generatePostIdea: [
          {
            params: {
              brandName: this.brand.brandName,
              segment: this.brand.segment,
              brandTargetAudience: this.brand.brandTargetAudience
            }
          },
          {
            idea: true
          }
        ]
      })

      if (!response.generatePostIdea) throw new Error('Não foi possível gerar descrição de post')

      this.postDescription = response.generatePostIdea.idea
    } catch (e) {
      toastifyError({ message: e })
    } finally {
      this.isLoading = false
    }
  }

  async getData() {
    const [formats] = await Promise.all([
      this.service.getFormats()
    ]);

    this.formats = formats;

    this.displayFormats = formats.map((f) => {
      return {
        label: f.alias,
        value: f.id
      } as IFormInputOption
    });

    await this.getBrandInfo()
  }

  async viewMoreImages() {
    if (window.innerWidth > 768) {
      this.isViewMoreImages = true
    } else {
      const { dialog } = await this.dialogService.open({
        component: () => ViewMoreImagesDialog,
        model: {
          availableImages: this.availableImages,
          displayFormats: this.displayFormats,
          selectedFormat: this.selectedFormat,
          formats: this.formats,
          selectedTemplate: this.selectedTemplate
        }
      })

      const result = await dialog.closed

      if (result.status == 'ok') {
        const value = result.value as {
          availableImages: ModelTypes['pTemplate'][]
          displayFormats: IFormInputOption[]
          selectedFormat: string
          formats: SizeObject[]
          selectedTemplate: ModelTypes["pTemplate"]
        }

        this.availableImages = value.availableImages
        this.displayFormats = value.displayFormats
        this.selectedFormat = value.selectedFormat
        this.formats = value.formats
        this.selectedTemplate = value.selectedTemplate

        this.advanceStep()
      }
    }
  }

  selectTemplate(image) {
    this.selectedTemplate = image
  }

  @watch('selectedFormat')
  private async getAvailableDisplays(): Promise<ModelTypes["pTemplate"][]> {
    if (!this.selectedFormat) return;

    const dimmension = this.formats.find(x => x.id === this.selectedFormat);

    const result = await this.service.getTemplates({ height: dimmension.height, width: dimmension.width });

    this.availableImages = result
  }

  navigateStep(step: number) {
    if (!this.steps[step].isCompleted || this.currentStep == step) return

    this.currentStep = step;
  }

  advanceStep() {

    this.steps.at(this.currentStep).isCompleted = true

    this.currentStep += 1
  }

  async handleGenerateProject() {
    // const validateResult = await this.validationController.validate();

    // if (!validateResult.valid) return;
    if (!['user-premium', 'user-freetrial', 'copymagico-admin'].includes(this.authService.user?.defaultRole)) {
      await this.dialogService.open({
        component: () => GoPremiumDialog,
        model: {},
        lock: true,
        startingZIndex: 100,
      });

      return
    }
    // if (!this.selectedAudienceId) toastifyWarning({ message: "É necessário um público alvo." });

    await this.service.insertUserMedia(this.selectedImage);

    const result = await this.service.createNewProject(
      {
        brandId: this.brand.id,
        productId: null,
        templateId: this.selectedTemplate.id,
        image: this.selectedImage,
        artDirectional: this.postDescription,
      }
    )

    if (result) {
      GoogleAnalyticsHelper.trackEvent(GoogleAnalyticsEventAction.Click, GoogleAnalyticsEventCategory.Post, 'create-post');

      await this.router.load(`/editor/${result.projectId}`, {
        data: {
          brandId: this.brand.id,
          productId: null,
          prompt: this.postDescription,
        }
      });
    }
  }
}